import { Component, Inject } from '@angular/core'
import { Outsider } from '../../../models/outsider.interface'
import {
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { TrackingService } from '../../../services/tracking/tracking.service'
import { UploadfileService } from '../../../services/uploadfile/uploadfile.service'
import { CommonModule, DatePipe, NgFor, NgIf } from '@angular/common'
import {
    MatDialog,
    MAT_DIALOG_DATA,
    MatDialogRef,
} from '@angular/material/dialog'
import { ToastrService } from 'ngx-toastr'
import { PersonService } from '../../../services/person/persona.service'
import { LoadingService } from '../../../services/loadingService/loading.service'
import {
    DateAdapter,
    MatNativeDateModule,
    MatOption,
} from '@angular/material/core'
import { SelectFileComponent } from '../../select-file/select-file.component'
import { MatCardModule } from '@angular/material/card'
import {
    MatError,
    MatFormField,
    MatFormFieldModule,
    MatLabel,
} from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatTableModule } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { MatIcon, MatIconModule } from '@angular/material/icon'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatSelect, MatSelectModule } from '@angular/material/select'
import { SearchSelectComponent } from '../../../components/search-select/search-select.component'
import {
    civilStatus,
    conflictRelated,
    ethnic,
    identificationTypes,
    sex,
    territories,
} from '../../incident-observatorio/incident-form/incident.definitions'
import {
    authorities,
    authority,
    others,
    tags,
    victimizingFact,
    violenceTypeDescriptions,
} from '../../incident-madeja/incident-madeja/incident-madeja.definitions'
import { Incident } from '../../../models/incident.interface'
import { OptionSelect } from '../../search/search.component'
import { Violence, ViolenceType } from '../../../models/violence.interface'
import { Location } from '../../../models/location.interface'
import { IncidentService } from '../../../services/incident/incident.service'
import { LocationService } from '../../../services/location/location.service'
import { ViolenceService } from '../../../services/violence/violence.service'
import { FormUtils } from '../../../utils'
import { HttpErrorResponse } from '@angular/common/http'
import { MatStepperModule } from '@angular/material/stepper'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatRadioModule } from '@angular/material/radio'
import { EditorComponent } from '../../editor/editor.component'
import { Accompaniment } from '../../../models/accompaniment.interface'
import { AccompanimentService } from '../../../services/accompaniment/accompaniment.service'
import { typeStatement } from './accompaniment.definitions'

@Component({
    selector: 'app-tracking-form-madeja',
    standalone: true,
    imports: [
        MatStepperModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatIcon,
        MatIconModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSelectModule,
        MatFormFieldModule,
        CommonModule,
        MatCardModule,
        MatInputModule,
        MatTableModule,
        MatButtonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatOption,
        MatSelect,
        NgIf,
        NgFor,
        SearchSelectComponent,
        MatStepperModule,
        MatAutocompleteModule,
        EditorComponent,
    ],
    providers: [DatePipe],
    templateUrl: './accompaniment-form.component.html',
    styleUrl: './accompaniment-form.component.css',
})
export class AccompanimentFormComponent {
    selectedFile: File | null = null
    fileName: string = ''
    errorMsg: string[] | null = null
    selectedPerson: Outsider | null = null
    conflictRelated = conflictRelated
    identificationTypes = identificationTypes
    sex = sex
    civilStatus = civilStatus
    ethnic = ethnic
    territories = territories
    authority = authority
    victimizingFact = victimizingFact
    typeStatement = typeStatement
    tags = tags.map((t) => t.value)
    authorities: string[] = Object.keys(authorities)
    violenceTypeDescriptions = violenceTypeDescriptions
    others = others
    accompanimentId: string | null = null
    accompanimentSelected: Accompaniment | null = null
    violenceSelected: OptionSelect[] = []
    private fromPage = 1
    locations: Location[] = []
    listViolences: Violence[] = []
    listViolencesType: ViolenceType[] = []
    displayFn = (location: Location) =>
        `${location?.city}, ${location?.department ?? ''}`
    idFn = (location: Location) => location.id

    constructor(
        private fb: FormBuilder,
        private incidentService: IncidentService,
        private toastr: ToastrService,
        private personService: PersonService,
        private dialog: MatDialog,
        private uploadfileService: UploadfileService,
        private locationService: LocationService,
        private datePipe: DatePipe,
        private trackingService: TrackingService,
        private loadingService: LoadingService,
        private serviceViolence: ViolenceService,
        private serviceAccompaniment: AccompanimentService,
        private dialogRef: MatDialogRef<AccompanimentFormComponent>,
        private adapter: DateAdapter<Date>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.adapter.setLocale('es-CO')
    }

    private fetchViolence() {
        this.serviceViolence.getViolenceType().subscribe((data) => {
            this.listViolencesType = data
            this.violenceSelected =
                this.serviceViolence.getOptionViolenceType(data)
        })
    }

    ngOnInit() {
        this.fetchViolence()
        this.groupCommunityChanges()

        // arrays base
        this.accompanimentForm.setControl(
            'victims',
            this.outsiderForm.get('victimas') as FormArray
        )

        this.accompanimentForm.setControl(
            'perpetrors',
            this.secondOutsiderForm.get('agresores') as FormArray
        )

        this.addVictim()
        this.addAgressor()

        this.incidentService.asyncErrorValidation$.subscribe((error) => {
            if (error) {
                this.errorMsg = FormUtils.parseErrorMsgs(error)
            }
        })

        this.accompanimentForm
            .get('address.georeference')
            ?.valueChanges.subscribe((checked) => {
                const geoRefControl =
                    this.accompanimentForm.get('address.geoRef')
                if (!(geoRefControl instanceof FormGroup)) {
                    return
                }
                const geoRef = geoRefControl as FormGroup
                const fields = ['latitude', 'longitude']
                fields.forEach((fieldName) => {
                    const control = geoRef.get(fieldName)
                    if (!checked) {
                        control?.setValue(null)
                        control?.clearValidators()
                        control?.disable()
                    } else {
                        control?.setValidators([Validators.required])
                        control?.enable()
                    }
                    control?.updateValueAndValidity()
                })
            })

        // Cargar ubicaciones
        this.locationService.allLocation().subscribe((data) => {
            this.locations = data

            this.serviceAccompaniment.selectedAccompanimentSubject$.subscribe(
                (incident) => {
                    if (incident) {
                        this.accompanimentId = String(incident.id)
                        this.accompanimentSelected = incident

                        // Buscar ubicación asociada
                        const matched = this.locations.find(
                            (loc) => loc.id === incident.location?.id
                        )
                        const dateTime = this.incidentService.setDateTimeToForm(
                            incident.date
                        )

                        //Cargar los campos del incidente
                        this.accompanimentForm.patchValue({
                            description: incident.description ?? '',
                            address: incident.address ?? '',
                            statement: incident.statement ?? '',
                            location: matched ?? null,
                            date: dateTime.date,
                            time: dateTime.time,
                        })
                        //Cargar víctimas
                        const victimasArray = this.outsiderForm.get(
                            'victimas'
                        ) as FormArray
                        victimasArray.clear()

                        incident.victims?.forEach((victim, index) => {
                            const isEditing = true
                            const victimForm = this.newVictim(isEditing)

                            victimForm.patchValue({
                                ...victim,
                                birthDate: this.trackingService.toLocalDateOnly(
                                    victim.birthDate
                                ),
                                socioeconomicStatus: {
                                    ...victim.socioeconomicStatus,
                                },
                            })

                            // Calcular edad
                            victimForm
                                .get('birthDate')
                                ?.valueChanges.subscribe((date: Date) => {
                                    const age =
                                        this.incidentService.calculateAge(date)
                                    victimForm.get('age')?.setValue(age)
                                })

                            victimasArray.push(victimForm)
                        })

                        //Cargar agresores
                        const agresoresArray = this.secondOutsiderForm.get(
                            'agresores'
                        ) as FormArray
                        agresoresArray.clear()

                        incident.perpetrors?.forEach((perp, index) => {
                            const agresorForm = this.newAgressor(true)
                            agresorForm.patchValue(perp)
                            agresoresArray.push(agresorForm)
                            this.fillAggressorFields(perp, index)
                        })

                        //Cargar metadata de agresores
                        const metadaData = this.perpetrorsMetadata
                        metadaData.clear()
                        ;(Array.isArray(incident.perpetrorsMetadata)
                            ? incident.perpetrorsMetadata
                            : []
                        ).forEach((metaData: any) => {
                            const g = this.newPerpetratorMetadata()
                            // Normalizar organization
                            let organization = metaData?.organization
                            if (typeof organization === 'string') {
                                try {
                                    organization = JSON.parse(organization)
                                } catch {
                                    organization = []
                                }
                            }

                            g.patchValue({
                                id: metaData?.id ?? null,
                                document: metaData?.document ?? 0,
                                relationship: metaData?.relationship ?? '',
                                organization: Array.isArray(organization)
                                    ? organization
                                    : [],
                            })

                            metadaData.push(g)
                        })

                        //Cargar tipos de violencia
                        const violenceArray = this.accompanimentForm.get(
                            'violence'
                        ) as FormArray
                        violenceArray.clear()
                        incident.violence?.forEach((violence) => {
                            const violenceGroup = this.fb.group({
                                name: [violence.name ?? ''],
                                violenceType: [
                                    violence.violenceType?.length
                                        ? violence.violenceType[0].id
                                        : null,
                                ],
                                description: [
                                    JSON.parse(violence.description ?? '[]'),
                                ],
                            })
                            violenceArray.push(violenceGroup)
                        })
                    }
                }
            )
        })
    }

    searchPerson = (value: string) => {
        return this.personService.searchPerson(value)
    }
    showPerson = (person: Outsider) =>
        `${person.identificationCode} - ${person.name} ${person.lastName}`
    personIdentifier = (person: Outsider) => person.identificationCode

    accompanimentForm = this.fb.group({
        violence: this.fb.array([this.createViolenceGroup()]),
        conflictRelated: ['', []],
        phisicAgression: [false],
        description: ['', []],
        witnessRelationship: ['', []],
        date: [null as Date | null],
        time: ['', []],
        location: [null as Location | null, []],
        address: ['', []],
        perpetrors: this.fb.array([]),
        perpetrorsMetadata: this.fb.array([]),
        victims: this.fb.array([]),
        statement: this.fb.group({
            id: [null as number | null],
            link: ['', []],
            name: ['', []],
            scope: ['', []],
            location: ['', []],
            author: ['', []],
        }),
        attachment: this.fb.control<File | null>(null),
    })

    outsiderForm = this.fb.group({
        victimas: this.fb.array([]),
    })

    attachmentForm = this.fb.group({})

    fillFields(person: Outsider, index: number) {
        const victimForm = this.victimas.at(index) as FormGroup

        const birthDateLocal = this.trackingService.toLocalDateOnly(
            person.birthDate ?? null
        )

        victimForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType ?? '',
            personRef: person.personRef ?? '',
            civilianStatus: person.socioeconomicStatus?.civilianStatus ?? '',
            eps: person.socialSecurity?.eps ?? '',
            culturaEthnic: person.socioeconomicStatus?.culturaEthnic ?? '',
            sex: person.sex ?? '',
            phone: person.phone ?? '',
            birthDate: birthDateLocal,
            email: person.email ?? '',
        })
        victimForm
            .get('socioeconomicStatus.gender')
            ?.setValue(person.socioeconomicStatus?.gender ?? '')
        victimForm
            .get('socioeconomicStatus.culturaEthnic')
            ?.setValue(person.socioeconomicStatus?.culturaEthnic ?? '')
    }
    secondOutsiderForm = this.fb.group({
        agresores: this.fb.array([]),
    })

    fillAggressorFields(person: Outsider, index: number) {
        const agresorForm = this.agresores.at(index) as FormGroup

        agresorForm.patchValue({
            name: person.name,
            lastName: person.lastName,
            identificationType: person.identificationType,
            personRef: person.personRef,
            phone: person.phone ?? '',
        })
        const metaGroup = this.perpetrorsMetadata.at(index) as FormGroup
        metaGroup?.patchValue({
            document: person.identificationCode || null,
        })
    }

    get victimas(): FormArray {
        return this.outsiderForm.get('victimas') as FormArray
    }
    get agresores(): FormArray {
        return this.secondOutsiderForm.get('agresores') as FormArray
    }

    get perpetrorsMetadata(): FormArray<FormGroup> {
        return this.accompanimentForm.get(
            'perpetrorsMetadata'
        ) as FormArray<FormGroup>
    }

    newVictim(isEditing: boolean = false): FormGroup {
        return this.fb.group({
            id: [null],
            personRef: [{ value: '', disabled: isEditing }],
            identificationCode: [{ value: null, disabled: isEditing }],
            identificationType: [{ value: '', disabled: isEditing }],
            name: [{ value: '', disabled: isEditing }],
            lastName: [{ value: '', disabled: isEditing }],
            groupOrCommunity: [{ value: false, disabled: isEditing }],
            birthDate: [{ value: '', disabled: isEditing }],
            placeBirth: [
                { value: null as Location | null, disabled: isEditing },
            ],
            age: [{ value: 0, disabled: isEditing }],
            sex: [{ value: '', disabled: isEditing }],
            phone: [{ value: '', disabled: isEditing }],
            email: [
                { value: '', disabled: isEditing },
                [
                    Validators.pattern(
                        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    ),
                ],
            ],
            profesion: this.fb.group({
                id: [null],
                companyName: [{ value: '', disabled: isEditing }],
                name: [{ value: '', disabled: isEditing }],
                isLeader: [{ value: false, disabled: isEditing }],
            }),
            socioeconomicStatus: this.fb.group({
                id: [null],
                isHeadOfHousehold: [{ value: false, disabled: isEditing }],
                isConflictVictim: [{ value: false, disabled: isEditing }],
                hasProtectionScheme: [{ value: false, disabled: isEditing }],
                civilianStatus: [{ value: '', disabled: isEditing }],
                gender: [{ value: '', disabled: isEditing }],
                culturaEthnic: [{ value: '', disabled: isEditing }],
                ownsLand: [{ value: false, disabled: isEditing }],
                isLandOwner: [{ value: false, disabled: isEditing }],
                placeWork: [
                    { value: null as Location | null, disabled: isEditing },
                ],
            }),
            address: this.fb.group({
                id: [null as number | null],
                complement: ['', []],
                street: [{ value: '', disabled: isEditing }],
                territory: [{ value: '', disabled: isEditing }],
                authority: [{ value: '', disabled: isEditing }],
                territoryCode: [{ value: 0, disabled: isEditing }],
                authorityCode: [{ value: 0, disabled: isEditing }],
            }),
        })
    }

    newAgressor(isEditing: boolean = false): FormGroup {
        return this.fb.group({
            id: [null],
            personRef: [{ value: '', disabled: isEditing }],
            identificationCode: [{ value: null, disabled: isEditing }],
            identificationType: [{ value: '', disabled: isEditing }],
            name: [{ value: '', disabled: isEditing }],
            lastName: [{ value: '', disabled: isEditing }],
            phone: [{ value: '', disabled: isEditing }],
            sex: [{ value: '', disabled: isEditing }],

            address: this.fb.group({
                id: [null],
                street: [{ value: '', disabled: isEditing }],
                number: [{ value: '', disabled: isEditing }],
                complement: [{ value: '', disabled: isEditing }],
            }),
        })
    }

    newPerpetratorMetadata(): FormGroup {
        return this.fb.group({
            id: [null],
            document: [0, []],
            organization: ['', []],
        })
    }

    addVictim(isEditing: boolean = false) {
        const victimForm = this.newVictim(isEditing)
        victimForm.get('birthDate')?.valueChanges.subscribe((date: Date) => {
            const age = this.incidentService.calculateAge(date)
            victimForm.get('age')?.setValue(age)
        })

        this.victimas.push(victimForm)
        const index = this.victimas.length - 1
        this.applyVictimState(index, isEditing)
        victimForm.get('groupOrCommunity')?.valueChanges.subscribe(() => {
            this.applyVictimState(index, isEditing)
        })

        victimForm.get('personRef')?.valueChanges.subscribe(() => {
            this.applyVictimState(index, isEditing)
        })
    }

    deleteVictim(index: number) {
        this.victimas.removeAt(index)
    }

    addAgressor() {
        this.agresores.push(this.newAgressor())
        this.perpetrorsMetadata.push(this.newPerpetratorMetadata())
    }

    deleteAgressor(index: number) {
        ;(this.accompanimentForm.get('perpetrors') as FormArray).removeAt(index)
        this.perpetrorsMetadata.removeAt(index)
    }

    openUploadModal(): void {
        const dialogRef = this.dialog.open(SelectFileComponent, {
            width: 'auto',
            height: 'auto',
            maxHeight: '80vh',
        })

        dialogRef.afterClosed().subscribe((file: File | null | undefined) => {
            if (file instanceof File) {
                this.selectedFile = file
                this.accompanimentForm.patchValue({ attachment: file })
                this.accompanimentForm.get('attachment')?.markAsDirty()
                this.accompanimentForm
                    .get('attachment')
                    ?.updateValueAndValidity()
            }
            if (file === null) {
                this.clearSelectedFile()
            }
        })
    }

    clearSelectedFile(): void {
        this.selectedFile = null
        this.accompanimentForm.patchValue({ attachment: null })
        this.accompanimentForm.get('attachment')?.markAsDirty()
        this.accompanimentForm.get('attachment')?.updateValueAndValidity()
    }

    get violence(): FormArray {
        return this.accompanimentForm.get('violence') as FormArray
    }

    createViolenceGroup(): FormGroup {
        return this.fb.group({
            name: ['', Validators.required],
            violenceType: [''],
            description: ['', []],
        })
    }

    addViolence(): void {
        this.violence.push(this.createViolenceGroup())
    }

    removeViolence(index: number): void {
        this.violence.removeAt(index)
    }

    getViolenceTypesForIndex(index: number): ViolenceType[] {
        const group = this.violence.at(index) as FormGroup
        const selectedCategory = group.get('name')?.value as string | null

        if (!selectedCategory) return []

        const category = selectedCategory.trim().toUpperCase()

        return (this.listViolencesType ?? []).filter(
            (t) => (t.category ?? '').trim().toUpperCase() === category
        )
    }

    getTagsForIndex(index: number): string[] {
        const group = this.violence.at(index) as FormGroup
        const selectedId = group.get('violenceType')?.value as number | null

        if (!selectedId) return []

        const selectedType = this.listViolencesType?.find(
            (violenceType) => violenceType.id === selectedId
        )

        if (!selectedType?.name) return this.others

        const category = selectedType.name
            .trim()
            .toUpperCase() as keyof typeof violenceTypeDescriptions

        return violenceTypeDescriptions[category] ?? this.others
    }

    onChangeViolence(index: number): void {
        const group = this.violence.at(index) as FormGroup
        group.get('violenceType')?.reset([], { emitEvent: false })
    }

    closeDialog() {
        this.dialogRef.close()
    }

    buildDateTime(raw: any): string | null {
        if (!raw.date) return null
        const date = new Date(raw.date)
        if (raw.time) {
            const [hours, minutes] = raw.time.split(':')
            date.setHours(+hours)
            date.setMinutes(+minutes)
        } else {
            date.setHours(0)
            date.setMinutes(0)
        }
        date.setSeconds(0)
        return this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ss')!
    }

    saveAll() {
        if (this.accompanimentForm.invalid) {
            this.toastr.error('Faltan campos por llenar.')
            return
        }
        const raw = this.accompanimentForm.getRawValue()
        const formattedVictims = (raw.victims || [])
            .filter((victim: any) =>
                this.incidentService.hasValidValues(victim)
            )
            .map((victim: any) => {
                return {
                    ...victim,
                    lastName: victim.lastName ?? '',
                    name: victim.name ?? '',
                    identificationType: victim.identificationType ?? '',
                    identificationCode: victim.identificationCode ?? null,
                    birthDate: victim.birthDate
                        ? this.datePipe.transform(
                              victim.birthDate,
                              'yyyy-MM-dd'
                          )
                        : null,
                    socioeconomicStatus: {
                        ...victim.socioeconomicStatus,
                    },
                    address: {
                        complement: victim.address?.complement ?? '',
                        street: victim.address?.street ?? '',
                        number: victim.address?.number ?? '',
                        territory: victim.address?.territory ?? '',
                        authority: victim.address?.authority ?? '',
                        territoryCode: victim.address?.territoryCode ?? 0,
                        authorityCode: victim.address?.authorityCode ?? 0,
                    },
                }
            })

        const filteredPerpetrors = (raw.perpetrors || []).filter((p: any) =>
            this.incidentService.hasValidValues(p)
        )

        //Filtrar metadatos
        const filteredPerpetrorsMetadata = (raw.perpetrorsMetadata || [])
            .filter((m: any) => this.incidentService.hasValidValues(m))
            .map((m: any) => {
                const organizationValue = Array.isArray(m.organization)
                    ? m.organization
                    : []
                return {
                    ...m,
                    organization: JSON.stringify(organizationValue),
                }
            })

        const filteredViolence = (raw.violence || [])
            .filter((m: any) => this.incidentService.hasValidValues(m))
            .map((m: any) => {
                const descriptionValue = Array.isArray(m.description)
                    ? m.description
                    : []
                return {
                    ...m,
                    description: JSON.stringify(descriptionValue),
                    violenceType: [m.violenceType || -1],
                }
            })

        // Payload
        let payload: any = {
            violence: filteredViolence,
            incident: this.data.incidentId,
            description: raw.description ?? '',
            witnessRelationship: raw.witnessRelationship ?? '',
            conflictRelated: raw.conflictRelated ?? '',
            phisicAgression: raw.phisicAgression ?? false,
            date: this.buildDateTime(raw),
            location: raw.location,
            address: raw.address,
            victims: formattedVictims,
            perpetrors: filteredPerpetrors,
            statement: raw.statement,
            perpetrorsMetadata: filteredPerpetrorsMetadata,
            ...(this.accompanimentId != null
                ? { id: Number(this.accompanimentId) }
                : {}),
        }
        const victimsWithNullId = formattedVictims.map((v) => ({
            identificationCode: v.identificationCode,
        }))

        // Quitar null/undefined en objetos anidados
        payload = this.incidentService.stripNullIds(payload)
        payload.victims = payload.victims.map((v: any, i: number) => ({
            ...v,
            identificationCode:
                victimsWithNullId[i]?.identificationCode ?? null,
        }))
        // FormData
        const formData = new FormData()
        formData.append('payload', JSON.stringify(payload))

        if (raw.attachment) {
            formData.append('file', raw.attachment, raw.attachment.name)
        }

        // Crear o editar
        this.loadingService.show(true)
        const request$ = this.accompanimentId
            ? this.serviceAccompaniment.updateAccompaniment(
                  Number(this.accompanimentId),
                  formData
              )
            : this.serviceAccompaniment.createAccompaniment(formData)

        request$.subscribe({
            next: () => {
                this.loadingService.show(false)
                this.toastr.success(
                    this.accompanimentId
                        ? 'El seguimiento fue editado correctamente!'
                        : 'El seguimiento fue creado correctamente!',
                    'Exitoso!',
                    { progressBar: true, timeOut: 5000 }
                )
                this.dialogRef.close(true)
            },
            error: (err: HttpErrorResponse) => {
                this.loadingService.show(false)

                const backendErrors = err.error
                const message =
                    this.incidentService.formatBackendErrors(backendErrors)

                this.toastr.error(
                    message || 'Error desconocido en el servidor.',
                    'Error',
                    {
                        progressBar: true,
                        timeOut: 6000,
                        enableHtml: true,
                    }
                )
            },
        })
    }

    openFile(incident: Incident): void {
        const id = incident?.fileAttach?.fileUrl
        const name = incident?.fileAttach?.fileName || 'archivo.pdf'
        if (!id) return
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se logro descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openIncidentPdf(): void {
        const id = this.accompanimentSelected?.fileAttach?.fileUrl
        const name =
            this.accompanimentSelected?.fileAttach?.fileName || 'archivo.pdf'
        if (!id) return
        this.uploadfileService.openPdfById(id, name).subscribe({
            error: () => {
                const msg = `No se logro descargar o previsualizar el archivo`
                this.toastr.error(msg, 'Error', {
                    progressBar: true,
                    timeOut: 5000,
                })
            },
        })
    }

    openSelectedPdf(): void {
        if (!this.selectedFile) return
        if (this.selectedFile.type !== 'application/pdf') {
            const msg = `Solo podemos mostrarte archivos PDF`
            this.toastr.warning(msg, 'Aviso')
            return
        }
        this.uploadfileService.openLocalPdf(
            this.selectedFile,
            this.selectedFile.name
        )
    }

    hasFile(incident: Accompaniment | null): boolean {
        return (
            !!incident?.fileAttach?.fileUrl && !!incident?.fileAttach?.fileName
        )
    }

    isViolenceSelected(violenceType: number, index: number): boolean {
        if (!violenceType) {
            return false
        }

        const violenceArray = this.violence
        for (let i = 0; i < violenceArray.length; i++) {
            if (i !== index) {
                const group = violenceArray.at(i) as FormGroup
                const name = group.get('violenceType')?.value
                if (name === violenceType) {
                    return true
                }
            }
        }
        return false
    }
    getVictimaGroup(index: number) {
        return (this.accompanimentForm.get('victims') as FormArray).at(
            index
        ) as FormGroup
    }

    shouldDisableVictim(index: number, isEditing: boolean): boolean {
        return isEditing || this.isGroupOrCommunity(index)
    }

    applyVictimState(index: number, isEditing: boolean) {
        const victim = this.victimas.at(index) as FormGroup

        if (this.shouldDisableVictim(index, isEditing)) {
            victim.disable({ emitEvent: false })
        } else {
            victim.enable({ emitEvent: false })
        }
    }

    isGroupOrCommunity(index: number): boolean {
        const victim = (this.outsiderForm.get('victimas') as FormArray).at(
            index
        ) as FormGroup

        return (
            victim.get('groupOrCommunity')?.value ||
            victim.get('personRef')?.value === 'ISCOMUNITY'
        )
    }

    groupCommunityChanges() {
        const victimas = this.outsiderForm.get('victimas') as FormArray

        victimas.controls.forEach((group) => {
            const groupForm = group as FormGroup

            groupForm
                .get('groupOrCommunity')
                ?.valueChanges.subscribe((value: boolean) => {
                    groupForm.reset({}, { emitEvent: false })

                    if (value) {
                        groupForm.patchValue(
                            {
                                socioeconomicStatus: {
                                    isHeadOfHousehold: false,
                                    ownsLand: false,
                                    isLandOwner: false,
                                    hasProtectionScheme: false,
                                    isConflictVictim: false,
                                },
                                profesion: {
                                    isLeader: false,
                                },
                                socialSecurity: {
                                    hasInsurance: false,
                                },
                                education: {
                                    isIlliterate: false,
                                },
                                groupOrCommunity: true,
                                personRef: 'ISCOMUNITY',
                            },
                            { emitEvent: false }
                        )
                    }
                })
        })
    }

    onTerritorySelected(value: number, index: number) {
        const victimas = this.outsiderForm.get('victimas') as FormArray
        const victim = victimas.at(index) as FormGroup
        const territory = this.territories.find((t) => t.value === value)

        if (territory) {
            victim.patchValue({
                name: territory.label.toUpperCase(),
            })
        }
    }

    getVictimTerritories(index: number): string[] {
        const group = this.victimas.at(index) as FormGroup
        const address = group.get('address') as FormGroup

        const selectedAuthority = address.get('authority')?.value as
            | string
            | null

        if (!selectedAuthority) return []

        const authority = selectedAuthority
            .trim()
            .toUpperCase() as keyof typeof authorities

        return authorities[authority] ?? []
    }
}
